import { FC, useState } from 'react';
import { FaBoxOpen, FaFile, FaFolder } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/button/Button';
import { SectionIconLink } from '../../components/links/SectionIconLink';
import { DocumentListSection } from '../../components/oneDrive/DocumentList';
import { NavPage } from '../../components/page/Breadcrumbs';
import { PageContainer } from '../../components/page/PageContainer';
import { PageHeader } from '../../components/page/PageHeader';
import { PageSizer } from '../../components/page/PageSizer';
import { SectionHeader } from '../../components/page/SectionHeader';
import { useCustomerId } from '../../services/authentication/hooks/user';
import { useSections } from '../../services/graph/hooks/category';
import { useFilesInFolder } from '../../services/graph/hooks/documentHelper';
import { useIntroduction } from '../../services/graph/hooks/introduction';
import { CreateSectionModal } from './CreateSectionModal';
import strings from './documents.translations';

export const MyDocuments: FC = () => {
  const myDirectory = useCustomerId();
  const navigate = useNavigate();

  const breadcrumbs: NavPage[] = [
    {
      name: strings.common.pages.documents,
      to: '/documents'
    },
    {
      name: strings.myDocuments,
      to: '/documents/myDocuments'
    }
  ];

  const sections = useSections(myDirectory);

  const introduction = useIntroduction();

  if (!introduction.data) {
    throw Error('Could not get image data');
  }

  const [createOpen, setCreateOpen] = useState<boolean>(false);

  return (
    <PageContainer>
      <PageHeader
        title={strings.myDocuments}
        breadcrumbs={breadcrumbs}
        imageUrl={introduction.data.backgroundImages.documentsPage}
      ></PageHeader>
      <PageSizer className="py-5 flex flex-col gap-10">
        {sections.length > 0 && (
          <div>
            <SectionHeader title={strings.sections} Icon={FaFolder} className="mb-5">
              <Button primary onClick={() => setCreateOpen(true)}>
                {strings.common.create}
              </Button>
            </SectionHeader>
            <div className="grid gap-x-2 gap-y-2 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {sections.map(section => {
                if (!section.id) throw new Error('No section id');
                return <SectionIconLink driveItemId={section.id} key={section.id}></SectionIconLink>;
              })}
            </div>
          </div>
        )}
        <DocumentListSection
          getDocuments={() => useFilesInFolder(myDirectory)}
          deleteable
          filterable
          updateable
          title={strings.myDocuments}
          Icon={FaFile}
          customComponent={
            <Button Icon={FaBoxOpen} primary onClick={() => navigate('/documents/upload')}>
              {strings.uploadFile}
            </Button>
          }
        />
      </PageSizer>
      <CreateSectionModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </PageContainer>
  );
};
